import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useCallbackObservable, useObservable } from 'react-use-observable';

import { Alert, Typography } from 'antd';

import { of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import Modal from 'v2/components/Shared/Modal';
import RadioSelection from 'v2/components/Shared/RadioSelection';
import { sendEventToGA } from 'v2/helpers/analytics';
import { EAnticipationStatus } from 'v2/interfaces/user';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import { financialStatementService } from 'v2/services/financialStatement/statement';

import InstallmentCashbackAlert from '../InstallmentCashbackAlert';

export type AnticipationData = {
  credit_days: number;
  has_promotional_fee: boolean;
  custom_fee: {
    value: number;
  };
  anticipation_fees: {
    fixed_automatic_fee: number;
    dynamic_automatic_fee: number;
    fixed_manual_fee: number;
    dynamic_manual_fee: number;
  };
};

interface ICardListProps {
  open: boolean;
  setClose: () => void;
  anticipationData: AnticipationData;
  loadAnticipationData: () => void;
}

interface IWarningAnticipationBlockedProps {
  isAnticipationBlocked: boolean;
  warningClass: string;
}

interface IWarningPromotionInclusionProps {
  isAnticipationBlocked: boolean;
  warningClass: string;
  period: number;
  hasPromotion: boolean;
  promotionValue: number;
}

interface IWarningCommonAnticipationChangeProps {
  isAnticipationBlocked: boolean;
  warningClass: string;
  period: number;
  currentPeriod: number;
  hasPromotion: boolean;
  hasPromotionalFee: boolean;
}

interface IWarningLeavingAnticipationPromotionProps {
  isAnticipationBlocked: boolean;
  warningClass: string;
  period: number;
  currentPeriod: number;
  hasPromotion: boolean;
  hasPromotionalFee: boolean;
  promotionalFee: number;
}

const periodOptions = [
  { value: '2', label: '2 dias' },
  { value: '7', label: '7 dias' },
  { value: '14', label: '14 dias' },
  { value: '21', label: '21 dias' },
  { value: '30', label: '30 dias (padrão)' }
];

function WarningAnticipationBlocked({ isAnticipationBlocked, warningClass }: IWarningAnticipationBlockedProps) {
  if (isAnticipationBlocked) {
    return (
      <Alert
        type='error'
        className={warningClass}
        message='Antecipação bloqueada por alto índice de chargeback'
        showIcon
      />
    );
  }

  return null;
}

function WarningPromotionInclusion({
  isAnticipationBlocked,
  warningClass,
  period,
  hasPromotion,
  promotionValue
}: IWarningPromotionInclusionProps) {
  if (!isAnticipationBlocked && period === 2 && hasPromotion) {
    return (
      <Alert
        type='warning'
        className={warningClass}
        showIcon
        message={
          <>
            Você está aderindo a taxa <b>promocional</b> de antecipação de 2 dias, que é {promotionValue}% + 0.1% por
            dia antecipado, e passa a receber os valores de acordo com o novo prazo configurado. Deseja continuar?{' '}
            <b>
              Essa promoção é válida até o dia 30/11/2022. Caso confirme, a taxa promocional terá vigência até
              31/12/2022.
            </b>
          </>
        }
      />
    );
  }

  return null;
}

function WarningCommonAnticipationChange({
  isAnticipationBlocked,
  warningClass,
  period,
  currentPeriod,
  hasPromotion,
  hasPromotionalFee
}: IWarningCommonAnticipationChangeProps) {
  if (!isAnticipationBlocked && period !== currentPeriod && !hasPromotionalFee && !(hasPromotion && period === 2)) {
    return (
      <Alert
        type='warning'
        className={warningClass}
        showIcon
        message={
          <>
            Os ganhos futuros com data de crédito superior a {period} dias serão creditados em até 24h. Deseja realmente
            continuar?
          </>
        }
      />
    );
  }

  return null;
}

function WarningLeavingAnticipationPromotion({
  isAnticipationBlocked,
  warningClass,
  period,
  currentPeriod,
  hasPromotion,
  hasPromotionalFee,
  promotionalFee
}: IWarningLeavingAnticipationPromotionProps) {
  if (!isAnticipationBlocked && period !== currentPeriod && hasPromotionalFee && !(hasPromotion && period === 2)) {
    return (
      <Alert
        type='warning'
        className={warningClass}
        showIcon
        message={
          <>
            Ao continuar, você deixa de ter direito à taxa promocional no valor de {promotionalFee}% e passa a receber
            os valores de acordo com o novo prazo configurado e taxa comum de 2.99% + 0.1% por dia antecipado. Deseja
            continuar?
          </>
        }
      />
    );
  }

  return null;
}

function AnticipationPeriodModal({ open, setClose, anticipationData, loadAnticipationData }: ICardListProps) {
  const [period, setPeriod] = useState<number>(30);
  const [anticipationPromotion, setAnticipationPromotion] = useState(null);
  const [loadingChangePeriod, setLoadingChangePeriod] = useState(false);

  const handleChangePeriod = (value: string) => {
    sendEventToGA({
      category: 'Novo Extrato',
      action: 'Antecipação automática',
      label: `${value} dias`
    });

    setPeriod(parseInt(value));
  };

  const handleCloseButton = () => {
    setClose();

    if (anticipationData?.credit_days) {
      setPeriod(anticipationData.credit_days);
    }
  };

  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);
  const isAnticipationBlocked = user?.anticipation_status === EAnticipationStatus.BLOQUEADO;

  const [loadAnticipationPromotion] = useCallbackObservable(() => {
    return financialStatementService.getAnticipationPromotion().pipe(
      tap(({ data }) => {
        setAnticipationPromotion(data);
      }),
      catchError(error => {
        toast.error(error);
        return of(true);
      })
    );
  }, []);

  useEffect(() => {
    loadAnticipationPromotion();

    if (anticipationData?.credit_days) {
      setPeriod(anticipationData.credit_days);
    }
  }, [loadAnticipationPromotion, anticipationData]);

  const [changePeriod] = useCallbackObservable(() => {
    setLoadingChangePeriod(true);

    if (period !== anticipationData?.credit_days) {
      return financialStatementService.setAnticipationPeriod(period).pipe(
        tap(
          () => {
            setLoadingChangePeriod(false);
            loadAnticipationData();
            setClose();
            toast.success(
              `Alteração realizada com sucesso! Sua antecipação automática está configurada para ${period} dias após crédito em conta.`
            );
          },
          err => {
            setLoadingChangePeriod(false);
            toast.error(err);
            setPeriod(anticipationData?.credit_days);
          }
        )
      );
    } else {
      setLoadingChangePeriod(false);
      setClose();
      toast.error('Opção selecionada já é o período de antecipação automática atual.');
      return null;
    }
  }, [period]);

  const currentFixedFee = anticipationData?.anticipation_fees?.fixed_automatic_fee
    ? `${anticipationData?.anticipation_fees?.fixed_automatic_fee}%`
    : '2.99%';
  const currentDynamicFee = anticipationData?.anticipation_fees?.dynamic_automatic_fee ?? 0.1;

  return (
    <Modal
      open={open}
      setClose={handleCloseButton}
      textTitle='Antecipação automática'
      textReturnButton='Cancelar'
      textProceedButton='Alterar'
      handleProceedClick={changePeriod}
      isProceedButtonDisabled={isAnticipationBlocked}
      isProceedButtonLoading={loadingChangePeriod}
    >
      <div className='flex flex-col gap-4'>
        <RadioSelection
          formLabel='Programe em quantos dias deseja receber seus ganhos.'
          options={periodOptions}
          selectedValue={period.toString()}
          handleSelected={handleChangePeriod}
          isDisabled={isAnticipationBlocked}
        />

        <Typography.Text type='secondary'>
          * O recebimento antes do prazo incorre tarifa administrativa de {currentFixedFee} + {currentDynamicFee}% por
          dia antecipado.{' '}
          <Typography.Link
            href='https://ajuda.eduzz.com/hc/pt-br/articles/4402057357211-Como-funciona-a-antecipa%C3%A7%C3%A3o-%C3%93rbita-'
            target='_blank'
            rel='noopener noreferrer'
          >
            Saiba mais.
          </Typography.Link>
        </Typography.Text>

        <InstallmentCashbackAlert enabled={open} />

        <WarningAnticipationBlocked isAnticipationBlocked={isAnticipationBlocked} warningClass='' />
        <WarningPromotionInclusion
          isAnticipationBlocked={isAnticipationBlocked}
          warningClass=''
          period={period}
          hasPromotion={anticipationPromotion?.isActive}
          promotionValue={anticipationPromotion?.value}
        />
        <WarningCommonAnticipationChange
          isAnticipationBlocked={isAnticipationBlocked}
          warningClass=''
          period={period}
          currentPeriod={anticipationData?.credit_days}
          hasPromotion={anticipationPromotion?.isActive}
          hasPromotionalFee={anticipationData?.has_promotional_fee}
        />
        <WarningLeavingAnticipationPromotion
          isAnticipationBlocked={isAnticipationBlocked}
          warningClass=''
          period={period}
          currentPeriod={anticipationData?.credit_days}
          hasPromotion={anticipationPromotion?.isActive}
          hasPromotionalFee={anticipationData?.has_promotional_fee}
          promotionalFee={anticipationData?.custom_fee?.value}
        />
      </div>
    </Modal>
  );
}

export default AnticipationPeriodModal;
